import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { formatIsoDateShort } from "@/lib/bookingDates";
import { formatCurrency, parseAmount } from "@/lib/money";
import { usePageHeader } from "@/layout/usePageHeader";
import { StatCard, percentChange } from "@/pages/reports/reportShared";
import { Button } from "@/components/ui/Button";
import { Table } from "@/components/ui/Table";
import type {
  PriceSimulationImpact,
  PriceSimulationWorkspace,
  PriceSimulationWorkspaceArticle,
} from "@/types/camping";

type PeriodMode = "year" | "range";

function formatGross(value: string | null | undefined): string {
  if (value == null || value === "") return "—";
  return formatCurrency(value);
}

export function PriceSimulationPage() {
  const { setPageMeta } = usePageHeader();
  const currentYear = new Date().getFullYear();
  const [workspace, setWorkspace] = useState<PriceSimulationWorkspace | null>(null);
  const [draftPrices, setDraftPrices] = useState<Record<string, string>>({});
  const [periodMode, setPeriodMode] = useState<PeriodMode>("year");
  const [year, setYear] = useState(currentYear);
  const [fromDate, setFromDate] = useState(`${currentYear}-01-01`);
  const [toDate, setToDate] = useState(`${currentYear}-12-31`);
  const [impact, setImpact] = useState<PriceSimulationImpact | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadWorkspace = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<PriceSimulationWorkspace>("/api/v1/price-simulations/workspace")
      .then((data) => {
        setWorkspace(data);
        const next: Record<string, string> = {};
        for (const article of data.articles) {
          if (article.simulated_price_gross != null) {
            next[article.article_id] = Number(article.simulated_price_gross).toFixed(2);
          }
        }
        setDraftPrices(next);
      })
      .catch((err: unknown) => {
        setWorkspace(null);
        setError(err instanceof Error ? err.message : "Arbeitsbereich konnte nicht geladen werden.");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPageMeta({
      title: "Preisfindung",
      subtitle: "Neue Preise simulieren und Umsatzwirkung prüfen",
    });
    loadWorkspace();
    return () => setPageMeta(null);
  }, [loadWorkspace, setPageMeta]);

  const currency = workspace?.currency ?? impact?.currency ?? "EUR";

  const changedCount = useMemo(() => {
    if (!workspace) return 0;
    return workspace.articles.filter((article) => {
      const draft = draftPrices[article.article_id]?.trim();
      if (!draft) return false;
      const parsed = parseAmount(draft);
      if (parsed == null) return false;
      const currentGross =
        article.simulated_price_gross != null
          ? Number(article.simulated_price_gross)
          : article.current_price_gross != null
            ? Number(article.current_price_gross)
            : null;
      return currentGross == null || Math.abs(parsed - currentGross) > 0.001;
    }).length;
  }, [workspace, draftPrices]);

  function setDraftPrice(articleId: string, value: string) {
    setDraftPrices((prev) => ({ ...prev, [articleId]: value }));
  }

  function resetDraftFromCurrent(article: PriceSimulationWorkspaceArticle) {
    if (article.current_price_gross == null) {
      setDraftPrices((prev) => {
        const next = { ...prev };
        delete next[article.article_id];
        return next;
      });
      return;
    }
    setDraftPrice(article.article_id, Number(article.current_price_gross).toFixed(2));
  }

  function clearDraft(articleId: string) {
    setDraftPrices((prev) => {
      const next = { ...prev };
      delete next[articleId];
      return next;
    });
  }

  async function saveOverrides(): Promise<PriceSimulationWorkspace | null> {
    if (!workspace) return null;
    setSaving(true);
    setError(null);
    try {
      const overrides = workspace.articles
        .map((article) => {
          const raw = draftPrices[article.article_id]?.trim();
          if (!raw) {
            if (article.simulated_price_gross != null) {
              return { article_id: article.article_id, gross_amount: null };
            }
            return null;
          }
          const gross = parseAmount(raw);
          if (gross == null) return null;
          return { article_id: article.article_id, gross_amount: gross };
        })
        .filter((item): item is { article_id: string; gross_amount: number | null } => item != null);

      const data = await api.put<PriceSimulationWorkspace>("/api/v1/price-simulations/overrides", {
        overrides,
      });
      setWorkspace(data);
      const next: Record<string, string> = {};
      for (const article of data.articles) {
        if (article.simulated_price_gross != null) {
          next[article.article_id] = Number(article.simulated_price_gross).toFixed(2);
        }
      }
      setDraftPrices(next);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Simulationspreise konnten nicht gespeichert werden.");
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function runAnalysis() {
    setAnalyzing(true);
    setError(null);
    setImpact(null);
    try {
      if (changedCount > 0) {
        const saved = await saveOverrides();
        if (!saved) return;
      }
      const payload =
        periodMode === "year"
          ? { year }
          : { from_date: fromDate, to_date: toDate };
      const result = await api.post<PriceSimulationImpact>(
        "/api/v1/price-simulations/impact",
        payload,
      );
      setImpact(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Umsatzvergleich fehlgeschlagen.");
    } finally {
      setAnalyzing(false);
    }
  }

  const periodLabel = useMemo(() => {
    if (!impact) return null;
    if (impact.from_date === `${impact.from_date.slice(0, 4)}-01-01` &&
        impact.to_date === `${impact.from_date.slice(0, 4)}-12-31`) {
      return `Jahr ${impact.from_date.slice(0, 4)}`;
    }
    return `${formatIsoDateShort(impact.from_date)} – ${formatIsoDateShort(impact.to_date)}`;
  }, [impact]);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold text-label">Preisfindung</h1>
          <Link
            to="/reports"
            className="text-sm text-accent hover:underline"
          >
            Zurück zu Auswertungen
          </Link>
        </div>
        <p className="text-sm text-label-secondary">
          Simulieren Sie neue Verkaufspreise nur für diese Seite. Die produktiven Artikelpreise
          unter Stammdaten bleiben unverändert.
        </p>
      </header>

      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      <section className="rounded-xl border border-separator bg-surface p-4 shadow-sm space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-base font-semibold text-label">Simulationspreise</h2>
            <p className="mt-1 text-sm text-label-secondary">
              Bruttopreise pro Artikel eingeben. Leer lassen = aktueller Produktivpreis.
            </p>
          </div>
          <Button
            type="button"
            className="w-full sm:w-auto"
            disabled={saving || loading || changedCount === 0}
            onClick={() => void saveOverrides()}
          >
            {saving ? "Speichern…" : `Simulationspreise speichern${changedCount > 0 ? ` (${changedCount})` : ""}`}
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-label-secondary">Artikel werden geladen…</p>
        ) : workspace && workspace.articles.length === 0 ? (
          <p className="text-sm text-label-secondary">Keine aktiven Artikel vorhanden.</p>
        ) : workspace ? (
          <Table>
            <thead>
              <tr>
                <th className="text-left">Artikel</th>
                <th className="text-right">Aktuell (brutto)</th>
                <th className="text-right">Simulation (brutto)</th>
                <th className="text-right">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {workspace.articles.map((article) => {
                const draft = draftPrices[article.article_id] ?? "";
                const hasSimulation = article.simulated_price_gross != null || draft.trim() !== "";
                return (
                  <tr key={article.article_id}>
                    <td>
                      <div className="font-medium text-label">{article.article_name}</div>
                      <div className="text-xs text-label-tertiary">
                        {article.article_number}
                        {article.category_name ? ` · ${article.category_name}` : ""}
                      </div>
                    </td>
                    <td className="text-right tabular-nums">
                      {formatGross(article.current_price_gross)}
                    </td>
                    <td className="text-right">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="eh-input ml-auto w-full max-w-[8rem] text-right tabular-nums"
                        placeholder={article.current_price_gross != null ? Number(article.current_price_gross).toFixed(2) : "—"}
                        value={draft}
                        onChange={(e) => setDraftPrice(article.article_id, e.target.value)}
                      />
                    </td>
                    <td className="text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          className="text-xs text-accent hover:underline"
                          onClick={() => resetDraftFromCurrent(article)}
                        >
                          Aktuell
                        </button>
                        {hasSimulation ? (
                          <button
                            type="button"
                            className="text-xs text-label-secondary hover:underline"
                            onClick={() => clearDraft(article.article_id)}
                          >
                            Löschen
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        ) : null}
      </section>

      <section className="rounded-xl border border-separator bg-surface p-4 shadow-sm space-y-4">
        <h2 className="text-base font-semibold text-label">Umsatzvergleich</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block text-label-secondary">Zeitraum</span>
            <select
              className="eh-input w-full"
              value={periodMode}
              onChange={(e) => setPeriodMode(e.target.value as PeriodMode)}
            >
              <option value="year">Kalenderjahr</option>
              <option value="range">Freier Zeitraum</option>
            </select>
          </label>
          {periodMode === "year" ? (
            <label className="block text-sm">
              <span className="mb-1 block text-label-secondary">Jahr</span>
              <input
                type="number"
                className="eh-input w-full"
                min={2000}
                max={2100}
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
              />
            </label>
          ) : (
            <>
              <label className="block text-sm">
                <span className="mb-1 block text-label-secondary">Von</span>
                <input
                  type="date"
                  className="eh-input w-full"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block text-label-secondary">Bis</span>
                <input
                  type="date"
                  className="eh-input w-full"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </label>
            </>
          )}
        </div>
        <Button
          type="button"
          className="w-full sm:w-auto"
          disabled={analyzing || saving}
          onClick={() => void runAnalysis()}
        >
          {analyzing ? "Berechne…" : "Umsatz vergleichen"}
        </Button>
      </section>

      {impact ? (
        <section className="space-y-4">
          <div>
            <h2 className="text-base font-semibold text-label">
              Ergebnis {periodLabel ? `· ${periodLabel}` : ""}
            </h2>
            <p className="mt-1 text-sm text-label-secondary">{impact.logic_note}</p>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard
              label="Umsatz (original)"
              value={formatCurrency(impact.actual_total, currency)}
            />
            <StatCard
              label="Umsatz (simuliert)"
              value={formatCurrency(impact.simulated_total, currency)}
            />
            <StatCard
              label="Differenz"
              value={formatCurrency(impact.delta, currency)}
              hint={
                impact.delta_percent != null
                  ? percentChange(
                      Number(impact.simulated_total),
                      Number(impact.actual_total),
                    ) ?? undefined
                  : undefined
              }
            />
          </div>

          {impact.articles.length > 0 ? (
            <div className="rounded-xl border border-separator bg-surface p-4 shadow-sm">
              <h3 className="mb-3 text-sm font-semibold text-label">Nach Artikel</h3>
              <Table>
                <thead>
                  <tr>
                    <th className="text-left">Artikel</th>
                    <th className="text-right">Abrechnungsmenge</th>
                    <th className="text-right">Original</th>
                    <th className="text-right">Simuliert</th>
                    <th className="text-right">Differenz</th>
                  </tr>
                </thead>
                <tbody>
                  {impact.articles.map((row) => (
                    <tr key={row.article_id}>
                      <td>
                        <div className="font-medium text-label">{row.article_name}</div>
                        <div className="text-xs text-label-tertiary">
                          {row.article_number} · {row.category_name}
                        </div>
                      </td>
                      <td className="text-right tabular-nums">{row.quantity_total}</td>
                      <td className="text-right tabular-nums">
                        {formatCurrency(row.actual_revenue, currency)}
                      </td>
                      <td className="text-right tabular-nums">
                        {formatCurrency(row.simulated_revenue, currency)}
                      </td>
                      <td className="text-right tabular-nums">
                        {formatCurrency(row.delta, currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-label-secondary">
              Im gewählten Zeitraum wurden keine Rechnungspositionen mit simulierten Artikeln gefunden.
            </p>
          )}
        </section>
      ) : null}
    </div>
  );
}
